import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Modal,
  TextField,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useAlert } from "react-alert";
import { v4 as uuidv4 } from "uuid";

import { makeRequest } from "../../util";
import { useModalStyles } from "./styles";

export const ConfigureImpressionPixelModal = ({
  open,
  onClose,
  onSave,
  dest,
  existingConfig,
}) => {
  const classes = useModalStyles();
  const alert = useAlert();

  const [configEnabled, setConfigEnabled] = useState(
    existingConfig.settings.freshpaint_pixel_config?.enabled || false
  );
  const [configConversionWindowDays, setConfigConversionWindowDays] = useState(
    existingConfig.settings.freshpaint_pixel_config?.conversion_window_days || 1
  );

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const closeConfirmationModal = () => {
    setConfirmationModalOpen(false);
  };

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    const id = uuidv4();

    const defaultConfig = {
      // This id may be overwritten by existing config
      id: id,
      dest_type: dest.key,
      name: dest.key,
      connection_mode: dest.connectionMode,
    };

    var settings = _.pickBy(existingConfig.settings, (value) => value !== "");
    settings = _.extend(settings, {
      freshpaint_pixel_config: {
        enabled: configEnabled,
        conversion_window_days: configConversionWindowDays,
      },
    });

    const destConfig = {
      ...defaultConfig,
      ...existingConfig,
      // Remove any values that are just a blank string as that's a filler value
      // we use from the backend.
      settings: settings,
    };

    makeRequest("save_destination_config", destConfig)
      .then(() => {
        onSave && onSave(dest, destConfig.id, destConfig);
        closeConfirmationModal();
        handleClose();
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to save destination.");
        console.log(error);
        Sentry.captureException(error);
      });
  };

  const isDisabled = () => {
    return (
      isNaN(configConversionWindowDays) ||
      configConversionWindowDays < 1 ||
      configConversionWindowDays > 30
    );
  };

  const getPixelConfigString = () => {
    const freshpaintPixelBaseURI =
      "https://freshpaint-impression.com/pixel.png";

    // If the destination isn't configured yet, short circuit here and return
    // nothing. In practice, this shouldn't happen, because the button to open
    // the model isn't available before the destination is configured.
    if (!existingConfig.id) {
      return "";
    }

    // These look like query parameters, but this isn't a proper URL—it's a
    // string that gets copied into an ad platform's "macro". We can't use URL
    // functionality like URLSearchParams to build it because we don't want a
    // string like "{auctionId}" to get URL encoded here.
    const params: string[] = [
      "env_id=" + existingConfig.env_id,
      "dest_id=" + existingConfig.id,
    ];
    for (let key in dest.impressionPixelParams) {
      params.push(key + "=" + dest.impressionPixelParams[key]);
    }

    return freshpaintPixelBaseURI + "?" + params.join("&");
  };

  const handleCopyPixelConfigString = () => {
    const copyText = getPixelConfigString();
    navigator.clipboard.writeText(copyText);
  };

  if (!open) {
    return null;
  }

  const configureImpressionPixelModal = (
    <Modal onClose={handleClose} open={open}>
      <Card className={clsx(classes.root, classes.smallModal)}>
        <CardHeader
          title={`Configure Impression Pixel: ${dest.name}`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <FormControl
            component="fieldset"
            className={clsx("my-5", "w-full", classes.formControl)}
          >
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    className="m-1"
                    checked={configEnabled}
                    disabled={false}
                    onChange={(event) => {
                      setConfigEnabled(event.target.checked);
                    }}
                    name="enabled"
                  />
                }
                label="Enable Freshpaint Impression Pixel"
              />
            </FormGroup>
          </FormControl>
          <FormControl
            component="fieldset"
            className={clsx("my-5", "w-full", classes.formControl)}
          >
            <FormGroup>
              <FormControlLabel
                className={clsx(classes.formControlLabel)}
                control={
                  <TextField
                    fullWidth
                    autoComplete="off"
                    value={configConversionWindowDays}
                    disabled={!configEnabled}
                    label="Conversion Window (days)"
                    variant="outlined"
                    type="number"
                    onChange={(event) => {
                      setConfigConversionWindowDays(Number(event.target.value));
                    }}
                    name="conversionWindowDays"
                  />
                }
                label=""
              />
            </FormGroup>
            <FormHelperText component="div">
              <div className="text-xs text-gray-600 leading-4 -mt-1">
                {
                  "Maximum age of a conversion to match on, in days. Must be between 1 and 30 days."
                }
              </div>
            </FormHelperText>
          </FormControl>
          <FormControl
            component="fieldset"
            className={clsx("my-5", "w-full", classes.formControl)}
          >
            <FormControlLabel
              className={clsx(classes.formControlLabel)}
              control={
                <>
                  <FormControl className={clsx(classes.configCopyField)}>
                    <TextField
                      id="pixel-config-string"
                      autoComplete="off"
                      value={getPixelConfigString()}
                      disabled={true}
                      label="Pixel Configuration URL"
                      variant="outlined"
                      type="text"
                      name="pixelConfigString"
                      size="small"
                    />
                  </FormControl>
                  <Button
                    className={clsx(classes.configCopyButton)}
                    color="primary"
                    onClick={() => {
                      handleCopyPixelConfigString();
                    }}
                    variant="contained"
                    disabled={!configEnabled}
                  >
                    Copy
                  </Button>
                </>
              }
              label=""
            />
          </FormControl>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={handleClose}>Close</Button>
          {onSave && (
            <Button
              color="primary"
              onClick={() => {
                handleSave();
              }}
              variant="contained"
              disabled={isDisabled()}
            >
              Save
            </Button>
          )}
        </CardActions>
      </Card>
    </Modal>
  );
  return <>{configureImpressionPixelModal}</>;
};

ConfigureImpressionPixelModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  dest: PropTypes.object,
  existingConfig: PropTypes.object.isRequired,
};
